import { getAuth } from "@clerk/remix/ssr.server";
import type { LoaderArgs } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import {
  Link,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { createDb } from "~/db/db-client.server";
import type { Env } from "./_index";
import { schools } from "~/db/schema/schools";
import { eq } from "drizzle-orm";
import { isNumeric } from "~/utils/utils";
import type { School } from "~/db/types";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import { TypographyH2 } from "~/components/ui/typography/h2";

export async function loader({ request, params, context }: LoaderArgs) {
  const { id } = params;

  const { userId } = await getAuth(
    { request, context, params },
    {
      secretKey:
        context.env && (context.env as Env)["CLERK_SECRET_KEY"]
          ? (context.env as Env)["CLERK_SECRET_KEY"]
          : "",
    }
  );

  if (!id) {
    throw new Response("ID of the recruiter is required", { status: 422 });
  }

  if (!isNumeric(id)) {
    throw new Response("ID should be numeric", { status: 422 });
  }

  const db = createDb(context);

  const school = (await db
    .select()
    .from(schools)
    .where(eq(schools.id, Number(id)))
    .get()) as School;

  if (!school) {
    throw new Response("Not found", { status: 404 });
  }

  return json({ school, userId });
}

export default function ProfilePage() {
  const { school } = useLoaderData() as { school: School; userId: string };

  return (
    <>
      <div>
        <div>
          {school?.coverPhoto ? (
            <img
              className="h-48 w-full object-cover object-top lg:h-80 rounded-sm"
              src={`../resource/img/${school.coverPhoto}`}
              alt=""
            />
          ) : (
            <div className="h-8 w-full lg:h-10 rounded-sm"></div>
          )}
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
            {school?.logo ? (
              <div className="flex">
                <img
                  className="h-24 w-24 rounded-lg ring-4 ring-white sm:h-32 sm:w-32 object-cover"
                  src={`../resource/img/${school.logo}`}
                  alt=""
                />
              </div>
            ) : null}
            <div className="mt-6 sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className={`mt-6 min-w-0 flex-1 sm:hidden md:block`}>
                <h1 className="truncate text-2xl font-bold text-gray-900">
                  {school.name}
                </h1>
              </div>
              <div className="mt-6 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
                <Link
                  to={school.website ? "https://" + school.website : "#"}
                  target="_blank"
                  type="button"
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <ExternalLinkIcon
                    className="-ml-0.5 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <span>Website</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-6 hidden min-w-0 flex-1 sm:block md:hidden">
            <h1 className="truncate text-2xl font-bold text-gray-900">
              {school.name}
            </h1>
          </div>
        </div>
      </div>
      <div>
        {/* Content section */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 mt-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <TypographyH2>About</TypographyH2>
            <div className="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row">
              <div className="lg:w-full lg:max-w-2xl lg:flex-auto">
                <p
                  dangerouslySetInnerHTML={{
                    __html: school.about ? school.about : "",
                  }}
                ></p>
              </div>
              <div className="lg:flex lg:flex-auto lg:justify-center">
                <dl className="w-64 space-y-8 xl:w-80">
                  <div
                    key={"location"}
                    className="flex flex-col-reverse gap-y-2"
                  >
                    <dt className="text-base leading-7 text-gray-600">
                      {school.state} -{school.country}
                    </dt>
                    <dd className="font-semibold tracking-tight text-gray-900">
                      {school.location}
                    </dd>
                  </div>
                  <div
                    key={"employees"}
                    className="flex flex-col-reverse gap-y-2"
                  >
                    <dt className="text-base leading-7 text-gray-600">
                      {"Employees"}
                    </dt>
                    <dd className="font-semibold tracking-tight text-gray-900">
                      {school.noOfEmployees}
                    </dd>
                  </div>
                  <div
                    key={"founded"}
                    className="flex flex-col-reverse gap-y-2"
                  >
                    <dt className="text-base leading-7 text-gray-600">
                      {"Founded"}
                    </dt>
                    <dd className="font-semibold tracking-tight text-gray-900">
                      {school.founded}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>Oops</h1>
        <p>Status: {error.status}</p>
        <p>{error.data}</p>
      </div>
    );
  } else {
    return (
      <div>
        <h1>Uh oh ...</h1>
        <p>Something went wrong.</p>
        <pre>{error.data}</pre>
      </div>
    );
  }
}
